import { Form, Link, redirect, useParams } from 'react-router';

import { FormProvider, getFormProps, useForm } from '@conform-to/react';
import { parseWithZod } from '@conform-to/zod';

import { getAppClient } from '@boc/framework/client';
import {
  OrderByNumberDocument,
  OrderByNumberQuery,
  OrderByNumberQueryVariables,
} from '@boc/framework/saleor-graphql';

import { Route } from './+types/route';

import { cn } from '~/styling';
import { urls } from '~/urls';

import { SALEOR } from '~/framework.server/saleor';

import TextInput from '~/local/checkout/components/form/TextInput';
import { buttonVariants } from '~/local/components/button';
import { orderTrackSchema } from '~/local/track-order/schema';

import { isMissingPiecesAvailable } from '~/lib/server/missing-pieces.server';

export function loader() {
  return { missingPiecesAvailable: isMissingPiecesAvailable() };
}

export default function Page({ loaderData, actionData }: Route.ComponentProps) {
  const params = useParams();
  const [form, fields] = useForm({
    id: 'track-order',
    lastResult: actionData,
    defaultValue: {
      email: '',
      orderNumber: undefined,
    },
    onValidate({ formData }) {
      const parsed = parseWithZod(formData, {
        schema: orderTrackSchema,
      });
      return parsed;
    },
  });

  return (
    <main className="w-full py-10">
      <div className="mb-10 flex items-center justify-center">
        <h1
          data-testid="trackorder-headding"
          className="font-heading-condensed text-primary-2-80 text-xl leading-7 font-medium tracking-tight"
        >
          Track Order
        </h1>
      </div>
      <div className="text-system-error mx-auto mb-3 w-1/2">
        {form.errors?.map((error) => <p key={error}>{error}</p>)}
      </div>
      <div className="mx-auto w-1/2">
        <Form method="post" {...getFormProps(form)}>
          <FormProvider context={form.context}>
            <div className="">
              <TextInput
                data-testid="trackorder-email"
                name={fields.email.name}
                label="Email"
                type="email"
              />
              <TextInput
                data-testid="trackorder-order-id"
                name={fields.orderNumber.name}
                label="Order Id"
                type="text"
              />
              <button type="submit" className={cn(buttonVariants({}), 'w-full')}>
                Track Order
              </button>
            </div>
          </FormProvider>
        </Form>

        {loaderData.missingPiecesAvailable && (
          <p className="text-primary-2-40 mt-6 text-center leading-5">
            Pieces missing from something you ordered?{' '}
            <Link to={urls.general.missingPieces(params)} className="underline">
              Tell us and we&apos;ll replace it
            </Link>
            .
          </p>
        )}
      </div>
    </main>
  );
}

export async function action({ request, params }: Route.ActionArgs) {
  const formData = await request.formData();

  const submission = parseWithZod(formData, { schema: orderTrackSchema });

  if (submission.status != 'success') {
    return submission.reply();
  }

  const { orderNumber, email } = submission.value;

  const client = getAppClient(SALEOR);
  let data;

  data = await client.request<OrderByNumberQuery, OrderByNumberQueryVariables>(
    OrderByNumberDocument,
    { filter: { numbers: [orderNumber] } }
  );

  const order = data?.orders?.edges?.[0]?.node;

  if (order && order.userEmail === email) {
    throw redirect(urls.orders.summary(params, { orderId: order.id }));
  }
  return submission.reply({ formErrors: ['Could not find matching Order Id or Email'] });
}
